import { getCustomModelTexts } from 'src/data/customModel';
import { ACTIVE_PROJECT_TYPE, type ProjectType } from 'src/data/project';
import {
  DRAFT_TOV_ID,
  formatToneOfVoiceDescription,
  getToneOfVoiceForGeneration,
  getToneOfVoiceTitle,
  getTovTexts,
  isPresetTov,
} from 'src/data/toneOfVoice';
import type { ID } from 'src/lib/collection';

import {
  AI_RECOMPOSE_MODE,
  type AiRecomposeMode,
  GENERATION_IMPROVE_MODE,
  type GenerationImproveMode,
  type Language,
} from './const';
import type { ImageStyleListData, ImproveParams, ToneOfVoiceListData, TovInput } from './types';

const MAKE_CASUAL_INSTRUCTIONS = 'Use more casual terminology but keep it similar.';
const MAKE_LONGER_INSTRUCTIONS = 'make it longer';
const MAKE_SHORTER_INSTRUCTIONS = 'make it shorter';

interface GenerationImproveParams {
  improveMode: GenerationImproveMode;
  recomposeMode?: AiRecomposeMode;
  instructions?: string;
  tovInput?: TovInput;
  media?: MediaGenerationParameters;
}

interface MediaGenerationParameters {
  imageStyleId?: ID;
}

/**
 * Converts improve parameters to generation parameters based on the project type.
 */
export const getGenerationImproveParams = ({
  projectType,
  improveParams,
}: {
  projectType: ProjectType;
  improveParams: ImproveParams;
}): GenerationImproveParams | undefined => {
  // Prepare generation params for "document" project type
  if (projectType === ACTIVE_PROJECT_TYPE.document) {
    const recomposeMode = generationImproveModeToRecomposeModeShortForm(improveParams.improveMode);

    let instructions = improveParams.instructions;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeCasual) {
      instructions = MAKE_CASUAL_INSTRUCTIONS;
    } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeLonger) {
      instructions = MAKE_LONGER_INSTRUCTIONS;
    } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeShorter) {
      instructions = MAKE_SHORTER_INSTRUCTIONS;
    }

    let tovInput;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeTov && improveParams.tov) {
      const { tov, customModelId, draftToneOfVoice } = improveParams.tov;
      tovInput = getToneOfVoiceForGeneration(customModelId || tov, draftToneOfVoice);
    }

    let media;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle) {
      media = {
        imageStyleId: improveParams.imageStyleId,
      };
    }

    return {
      improveMode: improveParams.improveMode,
      recomposeMode,
      instructions,
      tovInput,
      media,
    };
  }

  // Prepare generation params for "blog" project type
  if (projectType === ACTIVE_PROJECT_TYPE.blogPostBuilder) {
    const recomposeMode = generationImproveModeToRecomposeModeLongForm(improveParams.improveMode);

    let instructions = improveParams.instructions;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeCasual) {
      instructions = MAKE_CASUAL_INSTRUCTIONS;
    }

    let tovInput;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeTov && improveParams.tov) {
      const { tov, customModelId, draftToneOfVoice } = improveParams.tov;
      tovInput = getToneOfVoiceForGeneration(customModelId || tov, draftToneOfVoice);
    }

    return {
      improveMode: improveParams.improveMode,
      recomposeMode,
      instructions,
      tovInput,
      media: undefined,
    };
  }

  // Prepare generation params for "long form" project type
  if (projectType === ACTIVE_PROJECT_TYPE.longFormBuilder) {
    const recomposeMode = generationImproveModeToRecomposeModeLongForm(improveParams.improveMode);

    let instructions = improveParams.instructions;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.makeCasual) {
      instructions = MAKE_CASUAL_INSTRUCTIONS;
    }

    let tovInput;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeTov && improveParams.tov) {
      const { tov, customModelId, draftToneOfVoice } = improveParams.tov;
      tovInput = getToneOfVoiceForGeneration(customModelId || tov, draftToneOfVoice);
    }

    let media;
    if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle) {
      media = {
        imageStyleId: improveParams.imageStyleId,
      };
    }

    return {
      improveMode: improveParams.improveMode,
      recomposeMode,
      instructions,
      tovInput,
      media,
    };
  }

  return undefined;
};

export const formatImproveParamsDescription = (
  improveParams: ImproveParams,
  {
    ToneOfVoiceListProps,
    ImageStyleListProps,
    language,
    getTitle,
  }: {
    ToneOfVoiceListProps: ToneOfVoiceListData;
    ImageStyleListProps: ImageStyleListData;
    language: Language;
    getTitle?: (mode: GenerationImproveMode) => string;
  }
) => {
  if (!improveParams?.improveMode) {
    return '';
  }

  const title = getTitle
    ? getTitle(improveParams.improveMode)
    : getGenerationImproveModeLabel(improveParams.improveMode);

  // in case of "manual instructions", just return the instructions
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.instructions) {
    return improveParams.instructions || title;
  }

  let description: string | undefined = '';
  if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeTov && improveParams.tov) {
    const { tov, customModelId, draftToneOfVoice } = improveParams.tov;

    if (tov === DRAFT_TOV_ID && draftToneOfVoice) {
      description = formatToneOfVoiceDescription(draftToneOfVoice);
    } else if (customModelId) {
      const { customModelTitle } = getCustomModelTexts(
        customModelId,
        ToneOfVoiceListProps.customModels
      );
      description = customModelTitle;
    } else if (isPresetTov(tov)) {
      description = getToneOfVoiceTitle(tov, language);
    } else {
      const { tovTitle } = getTovTexts(tov, ToneOfVoiceListProps.toneOfVoices, language);
      description = tovTitle;
    }
  } else if (improveParams.improveMode === GENERATION_IMPROVE_MODE.changeImageStyle) {
    const { generalStyles, customStyles } = ImageStyleListProps;
    const imageStyle = [...generalStyles, ...customStyles].find(
      (style) => style.id === improveParams.imageStyleId
    );
    description = imageStyle?.name || '';
  }

  return description ? `${title}: ${description}` : title;
};

const IMPROVE_RECOMPOSE_SHORT_FORM_MAP: Partial<Record<GenerationImproveMode, AiRecomposeMode>> = {
  [GENERATION_IMPROVE_MODE.doNotImprove]: AI_RECOMPOSE_MODE.keepAsIs,
  [GENERATION_IMPROVE_MODE.improveImage]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.changeImageStyle]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.makeCasual]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeLonger]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeShorter]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.changeTov]: AI_RECOMPOSE_MODE.tov,
  [GENERATION_IMPROVE_MODE.instructions]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.sfv]: AI_RECOMPOSE_MODE.sfv,
  [GENERATION_IMPROVE_MODE.formatGroups]: AI_RECOMPOSE_MODE.formatGroups,
};
export const generationImproveModeToRecomposeModeShortForm = (
  improveMode: GenerationImproveMode
): AiRecomposeMode | undefined => IMPROVE_RECOMPOSE_SHORT_FORM_MAP[improveMode];

const IMPROVE_RECOMPOSE_LONG_FORM_MAP: Partial<Record<GenerationImproveMode, AiRecomposeMode>> = {
  [GENERATION_IMPROVE_MODE.improveImage]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.changeImageStyle]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.improveEngaging]: AI_RECOMPOSE_MODE.relaxed,
  [GENERATION_IMPROVE_MODE.improveSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: AI_RECOMPOSE_MODE.refine,
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: AI_RECOMPOSE_MODE.strict,
  [GENERATION_IMPROVE_MODE.makeCasual]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.makeLonger]: AI_RECOMPOSE_MODE.expand,
  [GENERATION_IMPROVE_MODE.makeShorter]: AI_RECOMPOSE_MODE.shorten,
  [GENERATION_IMPROVE_MODE.changeTov]: AI_RECOMPOSE_MODE.tov,
  [GENERATION_IMPROVE_MODE.instructions]: AI_RECOMPOSE_MODE.instructionBased,
  [GENERATION_IMPROVE_MODE.sfv]: AI_RECOMPOSE_MODE.sfv,
  [GENERATION_IMPROVE_MODE.translate]: AI_RECOMPOSE_MODE.translate,
  [GENERATION_IMPROVE_MODE.formatGroups]: AI_RECOMPOSE_MODE.formatGroups,
};
export const generationImproveModeToRecomposeModeLongForm = (
  improveMode: GenerationImproveMode
): AiRecomposeMode | undefined => IMPROVE_RECOMPOSE_LONG_FORM_MAP[improveMode];

const GENERATION_IMPROVE_MODE_PROPS: Record<string, { label: string; description?: string }> = {
  [GENERATION_IMPROVE_MODE.doNotImprove]: {
    label: 'Keep as is',
  },
  [GENERATION_IMPROVE_MODE.continueWriting]: {
    label: 'Continue writing',
  },
  [GENERATION_IMPROVE_MODE.improveImage]: {
    label: 'Improve image performance',
    description: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.changeImageOverlay]: {
    label: 'Edit overlay text', // note label changes in `ChangeImageOverlayItem`
  },
  [GENERATION_IMPROVE_MODE.changeImageStyle]: {
    label: 'Change image style',
  },
  [GENERATION_IMPROVE_MODE.improveEngaging]: {
    label: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.improveSimilar]: {
    label: 'Make more engaging, but keep structure',
  },
  [GENERATION_IMPROVE_MODE.boostScoreEngaging]: {
    label: 'Generate higher scoring content',
    description: 'Make more engaging',
  },
  [GENERATION_IMPROVE_MODE.boostScoreSimilar]: {
    label: 'Generate higher scoring content, but keep structure',
    description: 'Fix spelling & grammar, replace a word where necessary',
  },
  [GENERATION_IMPROVE_MODE.makeCasual]: {
    label: 'Make more casual',
  },
  [GENERATION_IMPROVE_MODE.makeLonger]: {
    label: 'Make longer',
  },
  [GENERATION_IMPROVE_MODE.makeShorter]: {
    label: 'Make shorter',
  },
  [GENERATION_IMPROVE_MODE.changeTov]: {
    label: 'Change tone of voice',
  },
  [GENERATION_IMPROVE_MODE.sfv]: {
    label: 'Apply structure, format and vocabulary',
  },
  [GENERATION_IMPROVE_MODE.formatGroups]: {
    label: 'Apply AP style guide',
  },
  [GENERATION_IMPROVE_MODE.translate]: {
    label: 'Translate to another language',
  },
  [GENERATION_IMPROVE_MODE.instructions]: {
    label: 'Manual instructions',
  },
};

export const getGenerationImproveModeLabel = (type: GenerationImproveMode): string =>
  GENERATION_IMPROVE_MODE_PROPS[type]?.label;
export const getGenerationImproveModeDescription = (
  type: GenerationImproveMode
): string | undefined => GENERATION_IMPROVE_MODE_PROPS[type]?.description;

export const doesImproveModeAlwaysGenerateSingleVariation = (
  improveMode: GenerationImproveMode
): boolean => {
  const list: GenerationImproveMode[] = [
    GENERATION_IMPROVE_MODE.sfv,
    GENERATION_IMPROVE_MODE.formatGroups,
  ];
  return !list.includes(improveMode);
};

export const shouldImproveScoresForMode = (improveMode: GenerationImproveMode): boolean => {
  const modes: GenerationImproveMode[] = [
    GENERATION_IMPROVE_MODE.boostScoreEngaging,
    GENERATION_IMPROVE_MODE.boostScoreSimilar,
  ];
  return modes.includes(improveMode);
};
